import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  ScanBarcode, TrendingDown, Bell, ShoppingBasket, MapPin, Sparkles,
  ArrowRight, Star, Check, Quote,
} from "lucide-react";
import logoSquare from "@/assets/prico_logo_square.png.asset.json";

const storeBrands = ["Euro", "Ara", "D1", "Olímpica", "Éxito", "Jumbo", "Carulla", "Farmatodo", "Cruz Verde", "Pasteur"];

const DEBUG = true;

if (DEBUG) {
  const API_VERSION = (import.meta.env.VITE_API_VERSION as string) ?? "v1";
  const APP_BACKEND_URL = (import.meta.env.VITE_APP_BACKEND_URL as string) ?? "";
  const STAGE = (import.meta.env.VITE_STAGE as string) ?? "prod";
  
  console.log('');
  console.log('** HomePage **');
  console.log('STAGE:', STAGE);
  console.log('API_VERSION:', API_VERSION);
  console.log('APP_BACKEND_URL:', APP_BACKEND_URL);
  console.log('');
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "PRICO — Compara precios, ahorra inteligente" },
      { name: "description", content: "Registra, compara y ahorra. PRICO te dice dónde están los mejores precios de tu mercado, medicinas y artículos del hogar." },
      { property: "og:title", content: "PRICO — Compara precios, ahorra inteligente" },
      { property: "og:description", content: "Registra, compara y ahorra con PRICO." },
      { property: "og:url", content: "/" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: HomePage,
});

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-warm)" }} />
        <div className="absolute -top-32 -right-32 size-[480px] rounded-full blur-3xl opacity-50" style={{ background: "var(--primary)" }} />
        <div className="absolute -bottom-40 -left-32 size-[420px] rounded-full blur-3xl opacity-30" style={{ background: "var(--primary-glow)" }} />

        <div className="container-prico py-20 md:py-28 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <motion.div initial="initial" animate="whileInView" variants={fadeUp} className="space-y-6">
            <span className="chip">
              <Sparkles className="size-3.5" /> Nuevo · Asistente IA Premium
            </span>
            <h1 className="text-5xl md:text-7xl font-black leading-[0.95]">
              El mejor precio,{" "}
              <span className="relative inline-block">
                <span style={{ background: "var(--gradient-primary)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  siempre a la mano.
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none">
                  <path d="M2 8 Q 150 -4 298 8" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
              PRICO es la red social del mejor precio. Compara mercados, droguerías y tiendas en segundos.
              Escanea, registra y deja que la comunidad te ayude a ahorrar.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/productos" className="btn-primary">
                Descubrir PRICO <ArrowRight className="size-4" />
              </Link>
              <Link to="/contacto" className="btn-ghost">Contáctanos</Link>
            </div>
            <div className="flex items-center gap-6 pt-6">
              <div className="flex -space-x-2">
                {[1,2,3,4].map((i) => (
                  <div key={i} className="size-9 rounded-full border-2 border-background grid place-items-center text-xs font-bold text-primary-foreground"
                    style={{ background: `linear-gradient(135deg, hsl(${i*40} 80% 60%), var(--primary))` }}>
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-primary">
                  {[...Array(5)].map((_, i) => <Star key={i} className="size-4 fill-current" />)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">+12.000 ahorradores activos</p>
              </div>
            </div>
          </motion.div>

          {/* Floating phone mock */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: -4 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto"
          >
            <div className="absolute inset-0 -m-8 rounded-[3rem] blur-3xl opacity-50" style={{ background: "var(--gradient-primary)" }} />
            <div className="relative animate-float">
              <div className="w-[300px] md:w-[340px] aspect-[9/19] rounded-[3rem] p-3 shadow-glow" style={{ background: "var(--ink)" }}>
                <div className="h-full w-full rounded-[2.4rem] bg-background overflow-hidden flex flex-col">
                  <div className="bg-primary text-primary-foreground p-5 pb-8 relative">
                    <div className="flex items-center justify-between text-xs opacity-80">
                      <span>9:41</span>
                      <span>● ● ●</span>
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                      <img src={logoSquare.url} alt="" className="size-10 rounded-xl bg-white p-1" />
                      <div>
                        <p className="text-xs opacity-80">Hola, Camila 👋</p>
                        <p className="font-bold">Tu lista semanal</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 -mt-5 flex-1 space-y-3">
                    {[
                      { name: "Arroz Diana 1kg", best: "Olímpica", price: "$4.200", save: "-18%" },
                      { name: "Leche Alpina 1L", best: "Éxito", price: "$4.890", save: "-12%" },
                      { name: "Daflon 500mg", best: "Cruz Verde", price: "$48.900", save: "-22%" },
                    ].map((it, i) => (
                      <motion.div
                        key={it.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + i * 0.15 }}
                        className="card-soft p-3 flex items-center gap-3"
                      >
                        <div className="size-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
                          <ShoppingBasket className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{it.name}</p>
                          <p className="text-xs text-muted-foreground">Mejor en {it.best}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{it.price}</p>
                          <p className="text-[10px] font-bold text-[var(--success)]">{it.save}</p>
                        </div>
                      </motion.div>
                    ))}
                    <div className="card-soft p-3 flex items-center gap-3" style={{ background: "var(--gradient-warm)" }}>
                      <Sparkles className="size-5 text-primary" />
                      <p className="text-xs font-medium">Ahorras <b>$23.400</b> esta semana</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="btn-primary w-full text-sm">Ver ruta de compra <ArrowRight className="size-4" /></div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-background py-6 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap gap-12 text-2xl md:text-3xl font-display font-bold text-muted-foreground/70">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-12">
              {storeBrands.map((s) => (
                <span key={s + k} className="flex items-center gap-12">
                  {s} <span className="text-primary">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="container-prico py-24">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
          <span className="chip">Cómo funciona</span>
          <h2 className="text-4xl md:text-5xl font-black mt-4">
            Tres pasos para gastar menos
          </h2>
          <p className="text-muted-foreground mt-3">
            Sin complicaciones. Sin spam. Solo el mejor precio en tu bolsillo.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ScanBarcode, title: "Escanea", desc: "Lee el código de barras o agrega productos en segundos.", n: "01" },
            { icon: TrendingDown, title: "Compara", desc: "Mira el histórico y dónde está el mejor precio hoy.", n: "02" },
            { icon: MapPin, title: "Ahorra", desc: "Recibe tu ruta de compra organizada por tienda.", n: "03" },
          ].map((f, i) => (
            <motion.article
              key={f.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card-soft p-8 group relative overflow-hidden"
            >
              <span className="absolute top-4 right-5 font-display text-6xl font-black text-primary/10 group-hover:text-primary/20 transition">{f.n}</span>
              <div className="size-14 rounded-2xl grid place-items-center text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
                <f.icon className="size-7" />
              </div>
              <h3 className="text-2xl font-bold mt-5">{f.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm">{f.desc}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section className="py-24" style={{ background: "var(--gradient-warm)" }}>
        <div className="container-prico grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp} className="space-y-5">
            <span className="chip"><Bell className="size-3.5" /> Alertas inteligentes</span>
            <h2 className="text-4xl md:text-5xl font-black">
              Te avisamos cuando <em className="not-italic text-primary">baje el precio</em>.
            </h2>
            <p className="text-muted-foreground text-lg">
              Configura tus productos favoritos y deja que PRICO te notifique cuando aparezca
              una oferta cerca de ti. Nunca más pagues de más.
            </p>
            <ul className="space-y-3 pt-2">
              {[
                "Historial de precios por producto y por tienda",
                "Alertas geolocalizadas en tu barrio",
                "Asistente IA Premium (opcional, USD 5/mes)",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <span className="size-6 grid place-items-center rounded-full bg-primary text-primary-foreground shrink-0">
                    <Check className="size-3.5" />
                  </span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <div className="pt-2">
              <Link to="/productos" className="btn-primary">Ver todas las funciones <ArrowRight className="size-4" /></Link>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: "$2.4M", l: "Ahorro anual promedio" },
                { v: "12K+", l: "Usuarios activos" },
                { v: "350+", l: "Tiendas registradas" },
                { v: "98%", l: "Satisfacción" },
              ].map((s, i) => (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-soft p-6 text-center"
                >
                  <p className="font-display text-4xl font-black text-primary">{s.v}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.l}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-prico py-24">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <span className="chip">Testimonios</span>
          <h2 className="text-4xl md:text-5xl font-black mt-4">La comunidad PRICO habla</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "Camila R.", c: "Medellín", t: "Pasé de gastar $900K a $620K mensuales en mercado. Increíble." },
            { n: "Andrés M.", c: "Bogotá", t: "Las alertas son lo mejor. Ya no compro Daflon a precio lleno." },
            { n: "Laura P.", c: "Cali", t: "La ruta de compra me ahorra tiempo y dinero cada semana." },
          ].map((t, i) => (
            <motion.div key={t.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card-soft p-7 relative">
              <Quote className="size-8 text-primary/30 absolute top-5 right-5" />
              <div className="flex gap-1 text-primary mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="size-4 fill-current" />)}
              </div>
              <p className="text-foreground/90">"{t.t}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="size-10 rounded-full grid place-items-center text-primary-foreground font-bold" style={{ background: "var(--gradient-primary)" }}>
                  {t.n[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.n}</p>
                  <p className="text-xs text-muted-foreground">{t.c}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-prico pb-24">
        <motion.div {...fadeUp} className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center grain"
          style={{ background: "var(--gradient-ink)", color: "white" }}>
          <div className="absolute -top-20 -right-20 size-72 rounded-full blur-3xl opacity-40" style={{ background: "var(--primary)" }} />
          <span className="chip" style={{ background: "rgba(255,255,255,0.1)", color: "white", borderColor: "rgba(255,255,255,0.2)" }}>
            Empieza hoy
          </span>
          <h2 className="text-4xl md:text-6xl font-black mt-4 max-w-2xl mx-auto">
            Tu próxima compra puede ser un <span className="text-primary">25% más barata</span>.
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            Únete gratis a la red social del mejor precio. Sin tarjeta, sin compromiso.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/contacto" className="btn-primary">Quiero empezar a ahorrar <ArrowRight className="size-4" /></Link>
            <Link to="/productos" className="btn-ghost" style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.18)", color: "white" }}>
              Ver funciones
            </Link>
          </div>
        </motion.div>
      </section>
    </>
  );
}
